import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { sowService } from '../../services/sowService';
import { SOW } from '../../models/sow';
// ProcessingOverlay removed — async flow handles progress via polling
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  AlertTriangle,
  Home,
  Check,
  X,
} from 'lucide-react';
import { useAuthToken } from '../../components/AuthTokenProvider';

const clean = (text: string): string => {
  if (!text) return '';
  return text
    .replace(/\s*\([a-f0-9-]{8,}(?:,\s*[a-f0-9-]{8,})*\)/gi, '')
    .replace(/\b(?:image|photo|images|photos)\s+[a-f0-9-]{8,}/gi, 'photos')
    .replace(/\b[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\b/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\(\s*\)/g, '')
    .trim();
};


interface Improvement {
  title: string;
  reasoning: string;
  description: string;
  scope: string;
  impactedRooms: string[];
  imageIds?: string[];
}

interface SOWImage {
  imageId: string;
  url?: string;
  jobId?: string;
}

export const ScopeReviewPage: React.FC = () => {
  const { sowId } = useParams<{ sowId: string }>();
  const navigate = useNavigate();
  const { tokenSet, isLoading: tokenLoading } = useAuthToken();
  const isPublicMode = window.location.pathname.startsWith('/scope/');

  const [sow, setSOW] = useState<SOW | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [userNotes, setUserNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enabledJobs, setEnabledJobs] = useState<Set<string>>(new Set());
  const [enabledWholeHouse, setEnabledWholeHouse] = useState<Set<number>>(new Set());
  const [progress, setProgress] = useState<{ completed: number; total: number; step: string } | null>(null);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);

  useEffect(() => {
    if (sowId && (isPublicMode || (tokenSet && !tokenLoading))) {
      loadData();
    }
  }, [sowId, tokenSet, tokenLoading, isPublicMode]);

  // Poll while analyzing
  useEffect(() => {
    if (!sow || (sow as any).status !== 'analyzing') return;
    const interval = setInterval(async () => {
      try {
        const sowData = await sowService.getSOW(sowId!, !isPublicMode);
        // Update progress from backend
        if ((sowData as any).analyzingProgress) {
          setProgress((sowData as any).analyzingProgress);
        }
        if ((sowData as any).status !== 'analyzing') {
          setSOW(sowData);
          setEnabledJobs(new Set((sowData.jobs || []).map((j: any) => j.id)));
          const whImps = ((sowData as any).scopeImprovements || []).filter((imp: any) => imp.scope === 'whole_house');
          setEnabledWholeHouse(new Set(whImps.map((_: any, i: number) => i)));
          setLoading(false);
          clearInterval(interval);
        }
      } catch (err) { /* keep polling */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [sow, sowId, isPublicMode]);

  const loadData = async () => {
    if (!sowId) return;
    try {
      setLoading(true);
      const sowData = await sowService.getSOW(sowId, !isPublicMode);
      setSOW(sowData);
      if ((sowData as any).analyzingProgress) {
        setProgress((sowData as any).analyzingProgress);
      }
      if ((sowData as any).status === 'analyzing') {
        // Still processing — stay in loading state, polling will handle it
        return;
      }
      if ((sowData as any).status === 'scope_failed') {
        setError((sowData as any).scopeError || 'Scope generation failed. Please try again.');
        setLoading(false);
        return;
      }
      const jobs = sowData.jobs || [];
      setEnabledJobs(new Set(jobs.map((j: any) => j.id)));
      // Init whole-house toggles
      const whImps = ((sowData as any).scopeImprovements || []).filter((imp: any) => imp.scope === 'whole_house');
      setEnabledWholeHouse(new Set(whImps.map((_: any, i: number) => i)));
      setLoading(false);
    } catch (err) {
      setError('Failed to load scope data.');
      setLoading(false);
    }
  };

  const toggleJob = (jobId: string) => {
    setEnabledJobs(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (!sowId) return;
    try {
      setConfirming(true);
      const jobs = sow?.jobs || [];
      const disabledJobs = jobs.filter((j: any) => !enabledJobs.has(j.id)).map((j: any) => j.label);
      let notes = userNotes.trim();
      if (disabledJobs.length > 0) {
        notes = `SKIP THESE AREAS: ${disabledJobs.join(', ')}. ${notes}`;
      }

      // Confirm scope — saves work items from improvements
      await sowService.confirmScope(sowId, notes, !isPublicMode);

      // Navigate to overview — user can generate estimate from there
      navigate(isPublicMode ? `/scope/${sowId}` : `/sow/${sowId}`);
    } catch (err) {
      setError('Failed to confirm scope. Please try again.');
      setConfirming(false);
    }
  };

  const handleBack = () => {
    navigate(isPublicMode ? `/scope` : `/sow`);
  };

  const isAnalyzing = (sow as any)?.status === 'analyzing';

  if (loading || isAnalyzing || (!isPublicMode && (tokenLoading || !tokenSet))) {
    const stepLabel = progress?.step === 'generating_scope'
      ? 'Generating renovation scope...'
      : progress
        ? `Analyzing photo ${progress.completed} of ${progress.total}...`
        : isAnalyzing
          ? 'Starting analysis...'
          : 'Loading...';

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <Loader2 className="w-10 h-10 animate-spin text-gray-400 mx-auto mb-5" />
          <p className="text-lg font-medium text-gray-900 mb-2">
            {stepLabel}
          </p>
          {isAnalyzing && progress && progress.step === 'analyzing_images' && (
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-500 h-2.5 rounded-full transition-all duration-500"
                  style={{ width: `${Math.round((progress.completed / progress.total) * 100)}%` }}
                />
              </div>
              <p className="text-sm text-gray-500 mt-3">
                {progress.completed} of {progress.total} photos analyzed
              </p>
            </div>
          )}
          {isAnalyzing && progress?.step === 'generating_scope' && (
            <p className="text-sm text-gray-500 mt-2">
              All photos analyzed. Grouping rooms and identifying improvements...
            </p>
          )}
          {sow?.address && (
            <p className="text-sm text-gray-400 mt-4">{sow.address}</p>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertTriangle className="w-8 h-8 mx-auto text-red-500 mb-4" />
          <p className="text-gray-800 mb-4">{error}</p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
          </Button>
        </div>
      </div>
    );
  }

  const assessment = (sow as any)?.scopeAssessment || {};
  const improvements: Improvement[] = (sow as any)?.scopeImprovements || [];
  const jobs: any[] = sow?.jobs || [];
  const images: SOWImage[] = (sow as any)?.images || [];

  // Split improvements: whole-house vs room-specific
  const wholeHouseImprovements: Improvement[] = [];
  const roomImprovements = new Map<string, Improvement[]>();

  improvements.forEach(imp => {
    if (imp.scope === 'whole_house') {
      wholeHouseImprovements.push(imp);
    } else if (imp.impactedRooms?.[0]) {
      const room = imp.impactedRooms[0];
      if (!roomImprovements.has(room)) roomImprovements.set(room, []);
      roomImprovements.get(room)!.push(imp);
    }
  });


  const enabledCount = enabledJobs.size;
  const totalCount = jobs.length;

  const toggleWholeHouse = (index: number) => {
    setEnabledWholeHouse(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };


  const getJobImages = (jobId: string) =>
    images.filter(img => img.jobId === jobId && img.url).slice(0, 6);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-6 sm:px-6">

        <Button variant="ghost" onClick={handleBack} className="mb-4 -ml-2 text-gray-500 text-sm">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
        </Button>

        {/* Header */}
        <div className="mb-6">
          <p className="text-xs text-gray-400 mb-1 truncate">{sow?.address}</p>
          <h1 className="text-2xl font-bold text-gray-900">Renovation Scope</h1>
          {assessment.headline && (
            <p className="text-sm text-gray-600 mt-1">{assessment.headline}</p>
          )}
        </div>

        {/* Whole-house items — toggleable */}
        {wholeHouseImprovements.length > 0 && (
          <div className="mb-6">
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
              Whole property
            </div>
            <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
              {wholeHouseImprovements.map((imp, i) => {
                const enabled = enabledWholeHouse.has(i);
                return (
                  <div
                    key={i}
                    onClick={() => toggleWholeHouse(i)}
                    className="flex items-start gap-3 px-4 py-3 cursor-pointer"
                  >
                    <div className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      enabled ? 'bg-gray-900 border-gray-900' : 'bg-white border-gray-300'
                    }`}>
                      {enabled && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium ${enabled ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                        {imp.title}
                      </div>
                      {enabled && (
                        <div className="text-xs text-gray-500 mt-0.5">{clean(imp.description)}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Room cards */}
        <div className="mb-6">
          <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">
            By area
          </div>
          <div className="space-y-4">
            {jobs.map(job => {
              const enabled = enabledJobs.has(job.id);
              const roomImps = roomImprovements.get(job.label) || [];
              const jobImages = getJobImages(job.id);

              return (
                <div
                  key={job.id}
                  className={`rounded-xl border transition-all ${
                    enabled ? 'bg-white border-gray-200 shadow-sm' : 'bg-gray-50 border-gray-100'
                  }`}
                >
                  {/* Photos at top when enabled */}
                  {enabled && jobImages.length > 0 && (
                    <div className="flex gap-1 overflow-x-auto rounded-t-xl">
                      {jobImages.map((img, idx) => (
                        <div
                          key={img.imageId}
                          onClick={() => setExpandedImage(img.url || null)}
                          className={`flex-1 min-w-0 h-32 bg-gray-100 cursor-pointer hover:opacity-90 transition-opacity overflow-hidden ${
                            idx === 0 ? 'rounded-tl-xl' : ''
                          } ${idx === jobImages.length - 1 ? 'rounded-tr-xl' : ''}`}
                        >
                          <img src={img.url} alt="" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Header row — clickable toggle */}
                  <div
                    onClick={() => toggleJob(job.id)}
                    className="flex items-start gap-3 px-4 py-3 cursor-pointer"
                  >
                    <div className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      enabled ? 'bg-gray-900 border-gray-900' : 'bg-white border-gray-300'
                    }`}>
                      {enabled && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-base font-semibold ${enabled ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                        {job.label}
                      </div>
                      {/* When disabled, show a one-line summary of what work would be done */}
                      {!enabled && roomImps.length > 0 && (
                        <p className="text-xs text-gray-400 mt-0.5 truncate">
                          {roomImps.map(imp => clean(imp.title)).join(' · ')}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Work scope when enabled */}
                  {enabled && roomImps.length > 0 && (
                    <div className="px-4 pb-4 pt-0">
                      <div className="space-y-3">
                        {roomImps.map((imp, i) => (
                          <div key={i}>
                            <div className="text-sm font-medium text-gray-800">{clean(imp.title)}</div>
                            <div className="text-sm text-gray-500 mt-0.5 leading-relaxed">{clean(imp.description)}</div>
                            {imp.reasoning && (
                              <div className="text-xs text-gray-400 mt-1 italic">{clean(imp.reasoning)}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {enabled && roomImps.length === 0 && job.issues && job.issues.length > 0 && (
                    <div className="px-4 pb-4 pt-0">
                      <ul className="space-y-1">
                        {job.issues.map((issue: string, i: number) => (
                          <li key={i} className="text-sm text-gray-500 flex gap-2">
                            <span className="text-gray-300 mt-0.5">•</span>
                            <span>{issue}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Notes */}
        <div className="mb-6">
          <textarea
            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-transparent resize-none placeholder-gray-400 bg-white"
            rows={2}
            placeholder="Any adjustments? e.g., skip kitchen 2, just clean don't gut..."
            value={userNotes}
            onChange={(e) => setUserNotes(e.target.value)}
            disabled={confirming}
          />
        </div>

        {/* CTA */}
        <div className="pb-8">
          <Button
            onClick={handleConfirm}
            disabled={confirming || enabledCount === 0}
            size="lg"
            className="w-full bg-gray-900 hover:bg-gray-800 h-12 text-sm font-medium"
          >
            {confirming ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
            ) : (
              <>Generate Estimate <ArrowRight className="w-4 h-4 ml-2" /></>
            )}
          </Button>
          {(enabledCount < totalCount || enabledWholeHouse.size < wholeHouseImprovements.length) && (
            <p className="text-xs text-gray-400 text-center mt-2">
              Some items excluded from estimate
            </p>
          )}
        </div>
      </div>

      {/* Image lightbox */}
      {expandedImage && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setExpandedImage(null)}
        >
          <img
            src={expandedImage}
            alt=""
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>
      )}
    </div>
  );
};
